<template>
  <v-container v-if="preview"
               fluid>

    <page-title>
      Email Preview #{{ preview.id }}
      <template v-slot:buttons>
        <v-btn v-if="hasIncompleteResults"
               @click.prevent="fetchData">
          Refresh
        </v-btn>
        <v-btn color="primary"
               @click.prevent="downloadHTML">
          Download HTML
        </v-btn>
      </template>
    </page-title>

    <v-card class="mb-4"
            flat
            tile>
      <v-card-text>
        <v-simple-table>
          <tbody>
          <tr>
            <th style="max-width: 100px;">
              Email template
            </th>
            <td>
              <template v-if="template">
                {{ template.name }}
              </template>
              <template v-else>
                {{ preview.share_template_id }}
              </template>
            </td>
          </tr>
          <tr>
            <th>
              Test Recipient
            </th>
            <td>
              <template v-if="contact">
                {{ contact.name }}
              </template>
              <template v-else>
                {{ preview.user_contact_id }}
              </template>
            </td>
          </tr>
          <tr>
            <th>
              Subject
            </th>
            <td>
              {{ preview.subject }}
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <div class="mb-4">
      <v-btn-toggle v-model="toggleCategory"
                    color="primary"
                    mandatory>
        <v-btn>
          All
        </v-btn>
        <v-btn>
          <v-icon left>
            fal fa-fw fa-mobile
          </v-icon>
          Phone
        </v-btn>
        <v-btn>
          <v-icon left>
            fal fa-fw fa-tablet
          </v-icon>
          Tablet
        </v-btn>
        <v-btn>
          <v-icon left>
            fal fa-fw fa-desktop
          </v-icon>
          Desktop
        </v-btn>
        <v-btn>
          <v-icon left>
            fal fa-fw fa-browser
          </v-icon>
          Web
        </v-btn>
      </v-btn-toggle>
    </div>

    <email-preview-results-card v-if="[0, 1].includes(toggleCategory)"
                                name="Phone"
                                :results="phoneResults"
                                :all-results="results"
                                @update="fetchData">
    </email-preview-results-card>

    <email-preview-results-card v-if="[0, 2].includes(toggleCategory)"
                                name="Tablet"
                                :results="tabletResults"
                                :all-results="results"
                                @update="fetchData">
    </email-preview-results-card>

    <email-preview-results-card v-if="[0, 3].includes(toggleCategory)"
                                name="Desktop"
                                :results="desktopResults"
                                :all-results="results"
                                @update="fetchData">
    </email-preview-results-card>

    <email-preview-results-card v-if="[0, 4].includes(toggleCategory)"
                                name="Web"
                                :results="webResults"
                                :all-results="results"
                                @update="fetchData">
    </email-preview-results-card>

    <email-preview-results-dialog v-if="result">
    </email-preview-results-dialog>

  </v-container>
</template>
<style>
pre {
  max-height: none;
}
</style>
<script>
import {mapGetters} from 'vuex';
import {find, isObject, values} from 'lodash';
import emailPreviewService from '../../services/emailPreviewService';
import shareTemplateService from '../../../../ml/services/shareTemplateService';
import userContactService from '../../services/userContactService';
import EmailPreviewThumbnail from './components/EmailPreviewThumbnailCard.vue';
import EmailPreviewResultsCard from './components/EmailPreviewResultsCard.vue';
import EmailPreviewResultsDialog from './components/EmailPreviewResultsDialog.vue';
import PageTitle from '../../components/PageTitle.vue';

export default {
  components: {PageTitle, EmailPreviewResultsDialog, EmailPreviewResultsCard, EmailPreviewThumbnail},
  data() {
    return {
      clients: [],
      contact: null,
      inlineCSS: null,
      loading: false,
      template: null,
      toggleCategory: 0,
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    ...mapGetters('emailPreviews', [
      'preview',
      'result',
      'results',
    ]),
    incompleteResults() {
      return this.results.filter(r => r.status !== 'Complete');
    },
    hasIncompleteResults() {
      return this.incompleteResults.length > 0;
    },
    previewID() {
      return parseInt(this.$route.params.previewID);
    },
    phoneClientIDs() {
      return this.clients.filter(c => c.is_mobile).map(c => c.id);
    },
    tabletClientIDs() {
      return this.clients.filter(c => c.is_tablet).map(c => c.id);
    },
    desktopClientIDs() {
      return this.clients.filter(c => c.is_desktop && c.category !== 'Web').map(c => c.id);
    },
    webClientIDs() {
      return this.clients.filter(c => c.is_web || c.category === 'Web').map(c => c.id);
    },
    phoneResults() {
      return this.results.filter(r => this.phoneClientIDs.includes(r.id));
    },
    tabletResults() {
      return this.results.filter(r => this.tabletClientIDs.includes(r.id));
    },
    desktopResults() {
      return this.results.filter(r => this.desktopClientIDs.includes(r.id));
    },
    webResults() {
      return this.results.filter(r => this.webClientIDs.includes(r.id));
    },
  },
  created() {
    this.fetchClients();
    this.fetchData();
  },
  methods: {
    getClient(clientID) {
      return find(this.clients, c => c.id === clientID);
    },
    fetchClients() {
      emailPreviewService.getClients()
        .then(data => {
          this.clients = data;
        })
    },
    fetchData() {
      this.loading = true;
      emailPreviewService.getPreview(this.previewID, true)
        .then(data => {
          this.$store.dispatch('emailPreviews/setPreview', data);

          if (isObject(data) && isObject(data.test_results)) {
            this.$store.dispatch('emailPreviews/setResults', values(data.test_results));
          }

          emailPreviewService.getInlineCSS(this.previewID)
            .then(data => {
              this.inlineCSS = data.content;
            })
            .catch()

          shareTemplateService.getTemplate(this.preview.share_template_id)
            .then(data => {
              this.template = data;
            })
            .catch()

          userContactService.getContact(this.preview.user_contact_id)
            .then(data => {
              this.contact = data;
            })
            .catch()
        })
        .catch(() => {
          this.$router.replace({name: 'admin.mailgun.email_previews'});
        })
        .finally(() => {
          this.loading = false;
        })
    },
    downloadHTML() {
      if (!this.inlineCSS) {
        return;
      }
      const data = this.inlineCSS;
      const blob = new Blob([data], {type: 'text/html'});
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `preview_${this.preview.id}.html`;
      link.dispatchEvent(new MouseEvent(`click`, {bubbles: true, cancelable: true, view: window}));
    },
  }
}
</script>
